/**
 * Synthetic keys and wireframe field lists shared by ordering (view model) and field rendering.
 */

/** Shown even when not yet present on the API payload (import / new edits). */
export const FRED_DETAIL_BUSINESS_DATA_KEYS: string[] = [
  'formData_interestRateType',
  'formData_usedAtAddress',
  'formData_sacReason',
  'formData_calcapLoanNumPrior',
  'formData_remainBalanceRestructure',
  'formData_amountIncreaseRequested',
  'formData_cspAppNumber',
];

export const LENDER_DETAIL_EXTRA_KEYS: string[] = ['formData_lenderContactId'];

/** Ensures Business Information rows render even when omitted from the initial API payload. */
export const BUSINESS_DETAIL_EXTRA_KEYS: string[] = [
  'businessEin',
  'formData_businessOrgType',
  'formData_businessCounty',
  'businessData_addressLine1',
  'businessData_city',
  'businessData_zipCode',
];

/**
 * Business Information fields edited on Application Details.
 * EIN, form of business, county, street, city, and zip save on the application.
 * Borrower Name, DBA, address line 2, and year opened save on the shared Business record.
 */
export const BUSINESS_INFORMATION_EDITABLE_KEYS: string[] = [
  'businessEin',
  'formData_businessOrgType',
  'formData_businessCounty',
  'businessData_addressLine1',
  'businessData_city',
  'businessData_zipCode',
];

/**
 * Section order: CAAT Loan Application Tab wireframe (Small Business).
 * Lender before Business; Business Data includes dissolved Jobs block; Premiums split from loan amounts.
 */
export const WIRE_FRAME_SECTION_ORDER = [
  'basic',
  'program',
  'lender',
  'business',
  'businessData',
  'loanDetails',
  'premiums',
  'dates',
  'demographics',
  'sedi',
  'sacSupplemental',
  'delivery',
  // 'flags' — Jul 23: Status Flags hidden for everyone (DB preserved)
  'other',
  /** Second "Other Information" header (Technical Assistance ID only). */
  'otherContinued',
  /** Approved applications only; Admin edit / others read-only. */
  'changeInTerms',
] as const;

/** Synthetic key: loan # in Loan Details row (CalCAP: `calcapLoanNumber`; else `lenderLoanNumber`). */
export const DETAIL_LENDER_LOAN_NUMBER_KEY = '__detail_lenderLoanNumber';

/** Wireframe: Lender name / contact phone / email — read-only on this tab (not Application scalars). */
export const DISPLAY_LENDER_NAME_KEY = '__display_lenderName';
export const DISPLAY_LENDER_CALCAP_ID_KEY = '__display_lenderCalcapId';
/** Editable on Application Details; PATCH body key → updates `Lender.otherOrganizationId`. */
export const LENDER_CALCAP_ID_STORAGE_KEY = 'lenderOtherOrganizationId';
export const DISPLAY_LENDER_CONTACT_PHONE_KEY = '__display_lenderContactPhone';
export const DISPLAY_LENDER_CONTACT_EMAIL_KEY = '__display_lenderContactEmail';

export const WIRE_FRAME_LENDER_DISPLAY_KEYS: string[] = [
  DISPLAY_LENDER_NAME_KEY,
  DISPLAY_LENDER_CALCAP_ID_KEY,
  DISPLAY_LENDER_CONTACT_PHONE_KEY,
  DISPLAY_LENDER_CONTACT_EMAIL_KEY,
];

/** ApplicationBusinessData fields surfaced in Business Information (wireframe); rest stay under Business Data. */
export const BUSINESS_SECTION_BUSINESSDATA_KEYS = new Set([
  'businessData_addressLine1',
  'businessData_city',
  'businessData_zipCode',
]);

/** Wireframe: Business Information — read-only / action (not on Application row). */
export const DISPLAY_BORROWER_NAME_KEY = '__display_businessBorrowerName';
export const DISPLAY_BUSINESS_DBA_KEY = '__display_businessDbaName';
export const ACTION_BUSINESS_DETAILS_KEY = '__action_businessDetails';
export const DISPLAY_ADDRESS_LINE2_KEY = '__display_businessAddressLine2';
export const DISPLAY_BUSINESS_START_YEAR_KEY = '__display_businessStartYear';

export const WIRE_FRAME_BUSINESS_EXTRA_KEYS: string[] = [
  DISPLAY_BORROWER_NAME_KEY,
  DISPLAY_BUSINESS_DBA_KEY,
  ACTION_BUSINESS_DETAILS_KEY,
  DISPLAY_ADDRESS_LINE2_KEY,
  DISPLAY_BUSINESS_START_YEAR_KEY,
];

/** These four write to the shared Business row. A save changes every loan for that business. */
export const SHARED_BUSINESS_PROFILE_LABELS: Record<string, string> = {
  [DISPLAY_BORROWER_NAME_KEY]: 'Borrower Name',
  [DISPLAY_BUSINESS_DBA_KEY]: 'DBA',
  [DISPLAY_ADDRESS_LINE2_KEY]: 'Address line 2',
  [DISPLAY_BUSINESS_START_YEAR_KEY]: 'Year business opened',
};

/** Business Data wireframe: same address snapshot as Business Information, labeled "Loan Monies Used:". */
export const BIZDATA_LOAN_ADDRESS_LINE1_KEY = '__bizdata_loanMonies_addressLine1';
export const BIZDATA_LOAN_CITY_KEY = '__bizdata_loanMonies_city';
export const BIZDATA_LOAN_COUNTY_KEY = '__bizdata_loanMonies_county';
export const BIZDATA_LOAN_ZIP_KEY = '__bizdata_loanMonies_zipCode';
/** Shown in Business Data row; source is ApplicationDemographics.veteranOwned. */
export const DISPLAY_BIZDATA_VETERAN_OWNED_KEY = '__display_bizdataVeteranOwned';

export const BIZDATA_LOAN_MIRROR_KEYS: string[] = [
  BIZDATA_LOAN_ADDRESS_LINE1_KEY,
  BIZDATA_LOAN_CITY_KEY,
  BIZDATA_LOAN_COUNTY_KEY,
  BIZDATA_LOAN_ZIP_KEY,
];

export const WIRE_FRAME_BIZDATA_EXTRA_KEYS: string[] = [
  ...BIZDATA_LOAN_MIRROR_KEYS,
  DISPLAY_BIZDATA_VETERAN_OWNED_KEY,
];

export const BIZDATA_MIRROR_TO_STORAGE: Record<string, string> = {
  [BIZDATA_LOAN_ADDRESS_LINE1_KEY]: 'businessData_addressLine1',
  [BIZDATA_LOAN_CITY_KEY]: 'businessData_city',
  [BIZDATA_LOAN_COUNTY_KEY]: 'businessData_county',
  [BIZDATA_LOAN_ZIP_KEY]: 'businessData_zipCode',
};

/** Ensures Business Data wireframe fields appear even when sparse on the payload. */
export const BUSINESS_DATA_DETAIL_EXTRA_KEYS: string[] = [
  ...WIRE_FRAME_BIZDATA_EXTRA_KEYS,
  'formData_averageAnnualRevenueLast3Years',
  'businessData_annualRevenue',
  'businessData_naicsCode',
  'businessData_censusTractNumber',
  'businessData_minorityOrWomenOwned',
  'businessData_businessOpened2Years',
];

export const CPCFA_SOURCED_FIELD_CONTROL_CLASS = 'border-start border-primary rounded-start';
